import React, { useState, useEffect, useCallback, useMemo, ChangeEvent, useRef } from 'react';
import { Code, Eye, FileText, Link, Mail, PlusCircle, Save, Trash2 } from 'lucide-react';
import ReactQuill from 'react-quill';
import GlobalDrawer from '../../components/GlobalDrawer';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import styled from 'styled-components';
import  PopupComponent  from '../../components/PopupComponent';
import EmailPreview from './PreviewModal';
import "react-quill/dist/quill.snow.css";
import GenericConfirmationModal from '../../components/GenericConfirmationModal';
import { createNewContent, deleteContent, updateContent } from '../../services/contentService';
import { sendTestEmail, hasGmailSendScope } from '../../services/emailService';
import logger, { Logger } from '../../utils/logger';

interface EmailData {
  title: string;
  tags: string[];
  subject: string;
  message: string;  // Rich text content
  html?: string;    // HTML content
  type?: string;
  files?: File[];
  personalizedFields?: Array<{value: string, label: string}>;
  isHtmlTemplate?: boolean;
}

interface PersonalizationField {
  value: string;
  label: string;
  type?: string;
}

interface Tag {
  name: string;
  value: string;
}

interface EmailDashboardProps {
  openEmailBox: boolean;
  setOpenEmailBox: (value: boolean) => void;
  activeId: string;
  handleFormSubmit: (data: EmailData) => Promise<void>;
  messageTag: Tag[];
  initialData: EmailData;
  onSuccessfulSubmit?: () => Promise<void>;
  availableFields?: PersonalizationField[];
}

// Styled Components
const StyledEmailEditor = styled.div`
  font-family: "Inter", sans-serif;
  background-color: #f8f9fa;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  padding: 20px;
  width: 100%;
  min-width: 800px;
`;

const FormField = styled.div`
  margin-bottom: 16px;

  label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #333;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-right: 48px;
`;

const Button = styled.button<{ primary?: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 8px;

  ${(props) =>
    props.primary
      ? `
    background-color: #3498db;
    color: white;
    &:hover {
      background-color: #2980b9;
    }
    svg {
      color: white;
    }
  `
      : `
    background-color: #e9ecef;
    color: #495057;
    &:hover {
      background-color: #dee2e6;
    }
  `}

  svg {
    margin-right: 6px;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.2s ease;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
  }

  &::placeholder {
    color: #6c757d;
  }

  &:hover {
    border-color: #adb5bd;
  }
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
  padding: 4px;
`;

const StyledTag = styled.span<{ active: boolean }>`
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) => (props.active ? "#3498db" : "#f0f2f5")};
  color: ${(props) => (props.active ? "white" : "#4b5563")};
  text-transform: capitalize;
  margin: 4px;
  font-weight: 500;
  letter-spacing: 0.3px;
  box-shadow: ${(props) =>
    props.active ? "0 2px 4px rgba(52,152,219,0.2)" : "none"};

  &:hover {
    background-color: ${(props) => (props.active ? "#2980b9" : "#e5e7eb")};
    transform: translateY(-1px);
  }
`;

const StyledReactQuill = styled(ReactQuill)`
  .ql-container {
    font-size: 14px;
    background: #fff;
    border-color: #ced4da;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    min-height: 200px;
  }

  .ql-editor {
    min-height: 200px;
    font-size: 14px;
    line-height: 1.5;
  }

  .ql-toolbar {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    background: #fff;
    border-color: #ced4da;
  }
`;

// Tab Styles
const TabContainer = styled.div`
  display: flex;
  gap: 4px;
  background: #f4f5f6;
  padding: 4px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const Tab = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? '#1a1d1f' : '#6f767e'};
  box-shadow: ${props => props.active ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'};

  &:hover {
    color: #1a1d1f;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const HtmlEditor = styled.textarea`
  width: 100%;
  min-height: 400px;
  padding: 12px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
  font-size: 13px;
  line-height: 1.6;
  border: 1px solid #ced4da;
  border-radius: 4px;
  resize: vertical;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
  }
`;

// Test Email Modal Styles
const TestEmailModal = styled.div`
  padding: 20px;
`;

const TestEmailInput = styled(Input)`
  margin-bottom: 16px;
`;

const TestEmailActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

const EmailDashboard: React.FC<EmailDashboardProps> = ({
  openEmailBox,
  setOpenEmailBox,
  activeId,
  handleFormSubmit,
  messageTag,
  initialData,
  onSuccessfulSubmit,
  availableFields = [],
}) => {
  Logger.log('EmailDashboard rendered:', { openEmailBox, activeId, initialData });

  // State management
  const [emailData, setEmailData] = useState<EmailData>(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isFieldDrawerOpen, setIsFieldDrawerOpen] = useState(false);
  const editorRef = useRef<ReactQuill | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'richtext' | 'html'>('richtext');
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);

  const [previewData, setPreviewData] = useState<{
    isOpen: boolean;
    subject: string;
    content: string;
    attachments?: File[];
  }>({
    isOpen: false,
    subject: '',
    content: '',
    attachments: undefined
  });

  // Get user details for Gmail scope check
  const userDetails = useSelector((state: any) =>
    state?.rootReducers?.userData?.userDetails
  );

  const canSendEmail = useMemo(() => {
    return hasGmailSendScope(userDetails);
  }, [userDetails]);

  // Reset form when initialData changes
  useEffect(() => {
    Logger.log('EmailDashboard useEffect - initialData changed:', initialData);
    if (openEmailBox) {
      // Normalize the data - if html field has content, move it to message field
      // and set the tab to HTML mode
      const hasHtmlContent = initialData.html && initialData.html.trim().length > 0;

      const normalizedData: EmailData = {
        ...initialData,
        message: hasHtmlContent
          ? (initialData.html || '')
          : (initialData.message || '')
      };

      Logger.log('Content detection:', {
        hasHtmlContent,
        originalHtmlLength: initialData.html?.length || 0,
        originalMessageLength: initialData.message?.length || 0,
        normalizedMessageLength: normalizedData.message?.length || 0
      });

      setEmailData(normalizedData);
      setActiveTab(hasHtmlContent ? 'html' : 'richtext');
      setHasUnsavedChanges(false);
    }
  }, [initialData, openEmailBox]);

  const reduxFields = useSelector((state: any) =>
    state?.rootReducers?.userData?.userDetails?.userPreference?.customForm?.map(
      (item: any) => ({
        value: item?.value,
        label: item?.name,
        type: item?.type,
      })
    ) || []
  );

  const handleDelete = useCallback(async () => {
    if (!activeId) return;

    try {
      const response = await deleteContent(activeId);

      if (response.status === 200) {
        toast.success('Email template deleted successfully');
        setOpenEmailBox(false);

        if (onSuccessfulSubmit) {
          await onSuccessfulSubmit();
        }
      } else {
        toast.error('Failed to delete email template');
      }
    } catch (error) {
      console.error('Error deleting email template:', error);
      toast.error('Error deleting email template');
    }
  }, [activeId, setOpenEmailBox, onSuccessfulSubmit]);

  const handleFieldInsertion = useCallback((field: PersonalizationField) => {
    Logger.log('Field Insertion Called:', { field, activeTab });

    if (activeTab === 'richtext' && editorRef.current) {
      try {
        const quill = editorRef.current.getEditor?.() ?? null;

        if (quill) {
          const range = quill.getSelection();

          if (range) {
            quill.insertText(range.index, `{{${field.value}}}`, 'user');
          } else {
            const textLength = quill.getText().length;
            quill.insertText(textLength, `{{${field.value}}}`, 'user');
          }
        }
      } catch (error) {
        console.error('Error in field insertion:', error);
      }
    } else if (activeTab === 'html') {
      // Insert into HTML at cursor position or end
      setEmailData(prev => ({
        ...prev,
        message: prev.message + `{{${field.value}}}`
      }));
    }

    setIsFieldDrawerOpen(false);
    setHasUnsavedChanges(true);
  }, [activeTab]);

  const validateForm = useCallback(() => {
    const errors: string[] = [];
    if (!emailData.title.trim()) errors.push("Title is required");
    if (!emailData.subject.trim()) errors.push("Subject is required");
    if (!emailData.message.trim()) errors.push("Email content is required");
    return errors;
  }, [emailData]);

  const handleInputChange = useCallback((key: keyof EmailData, value: string | string[]) => {
    Logger.log('handleInputChange:', { key, value });
    setEmailData(prev => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  }, []);

  const handleTagToggle = useCallback((tagValue: string) => {
    Logger.log('handleTagToggle:', tagValue);
    setEmailData(prev => ({
      ...prev,
      tags: [tagValue],
    }));
    setHasUnsavedChanges(true);
  }, []);

  const handleConfirmClose = useCallback(() => {
    setOpenEmailBox(false);
  }, [setOpenEmailBox]);

  const handlePreviewToggle = useCallback(() => {
    Logger.log('[handlePreviewToggle] Opening preview');

    setPreviewData({
      isOpen: !previewData.isOpen,
      subject: emailData.subject,
      content: emailData.message,
      attachments: emailData.files
    });
  }, [emailData, previewData.isOpen]);

  const insertUnsubscribeLink = useCallback(() => {
    Logger.log('[insertUnsubscribeLink] Called, activeTab:', activeTab);

    const unsubscribeHTML = `
      <p style="text-align: center; font-size: 12px; color: #666;">
        If you no longer wish to receive these emails, you can
        <a href="{{unsubscribe_link}}" style="color: #666; text-decoration: underline;">unsubscribe here</a>.
      </p>
    `;

    if (activeTab === 'html') {
      // Insert into HTML
      const currentContent = emailData.message;

      if (currentContent.includes('</body>')) {
        const newContent = currentContent.replace('</body>', `${unsubscribeHTML}</body>`);
        setEmailData(prev => ({ ...prev, message: newContent }));
      } else if (currentContent.includes('</html>')) {
        const newContent = currentContent.replace('</html>', `${unsubscribeHTML}</html>`);
        setEmailData(prev => ({ ...prev, message: newContent }));
      } else {
        setEmailData(prev => ({ ...prev, message: currentContent + unsubscribeHTML }));
      }

      setHasUnsavedChanges(true);
      toast.success('Unsubscribe link inserted');
    } else if (editorRef.current) {
      // Insert into rich text editor
      try {
        const quill = editorRef.current.getEditor();

        if (quill) {
          const range = quill.getSelection() || { index: quill.getText().length, length: 0 };
          const unsubscribeText = `If you no longer wish to receive these emails, you can unsubscribe by clicking here: {{unsubscribe_link}}`;
          quill.insertText(range.index, unsubscribeText, 'user');
          setHasUnsavedChanges(true);
          toast.success('Unsubscribe link inserted');
        }
      } catch (error) {
        console.error('Error inserting unsubscribe link:', error);
        toast.error('Failed to insert unsubscribe link');
      }
    }
  }, [activeTab, emailData.message]);

  const handleSave = useCallback(async () => {
    Logger.log('[handleSave] Called with data:', emailData);
    Logger.log('[handleSave] activeTab:', activeTab);

    const errors = validateForm();
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      return;
    }

    setIsSubmitting(true);
    try {
      // IMPORTANT: Save to the correct field based on active tab
      // - If HTML tab is active, save to 'html' field and clear 'message'
      // - If Rich Text tab is active, save to 'message' field and clear 'html'
      // This ensures the template is EITHER rich text OR HTML, never both
      const payload = {
        details: {
          title: emailData.title,
          tags: emailData.tags,
          subject: emailData.subject,
          ...(activeTab === 'html'
            ? {
                html: emailData.message,  // Save current content to html field
                message: ''               // Clear rich text field
              }
            : {
                message: emailData.message, // Save current content to message field
                html: ''                    // Clear html field
              }
          ),
          isHtmlTemplate: activeTab === 'html',
          tag: Array.isArray(emailData.tags) && emailData.tags.length > 0
            ? emailData.tags[0]
            : 'no_tag'
        },
        type: 'email_template'
      };

      Logger.log('[handleSave] Prepared API payload:', payload);

      let response;

      if (activeId && activeId.length > 1) {
        Logger.log(`[handleSave] UPDATING CONTENT - ID: ${activeId}`);
        const updatePayload = {
          details: payload.details
        };
        response = await updateContent(updatePayload, activeId);
      } else {
        Logger.log('[handleSave] CREATING NEW CONTENT');
        response = await createNewContent(payload);
      }

      Logger.log(`[handleSave] API response: ${response?.status}`, response?.data);

      if (response?.status === 200) {
        toast.success('Email template saved successfully!');
        setHasUnsavedChanges(false);
        setOpenEmailBox(false);

        if (onSuccessfulSubmit) {
          await onSuccessfulSubmit();
        }
      } else {
        console.error('[handleSave] API returned non-200 status:', response);
        toast.error('Error saving email template');
      }
    } catch (error) {
      console.error('[handleSave] Error saving template:', error);
      if (error instanceof Error) {
        toast.error(`Failed to save email template: ${error.message}`);
      } else {
        toast.error('Failed to save email template. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [emailData, validateForm, activeId, setOpenEmailBox, onSuccessfulSubmit, activeTab]);

  const handleSendTestEmail = useCallback(async () => {
    if (!testEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmailAddress)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSendingTestEmail(true);
    try {
      await sendTestEmail({
        to: testEmailAddress,
        subject: emailData.subject || 'Test Email',
        message: emailData.message,
        contentId: activeId || undefined
      });

      toast.success(`Test email sent to ${testEmailAddress}`);
      setShowTestEmailModal(false);
      setTestEmailAddress('');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send test email');
    } finally {
      setIsSendingTestEmail(false);
    }
  }, [testEmailAddress, emailData, activeId]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirmModal(true);
    } else {
      setOpenEmailBox(false);
    }
  }, [hasUnsavedChanges]);

  const modules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
    ],
  }), []);

  const headerActions = useMemo(() => (
    <ButtonGroup>
      <Button onClick={handlePreviewToggle}>
        <Eye size={16} /> Preview
      </Button>

      {canSendEmail && (
        <Button onClick={() => setShowTestEmailModal(true)}>
          <Mail size={16} /> Send Test
        </Button>
      )}

      {activeId && (
        <Button
          onClick={() => setShowDeleteModal(true)}
          style={{
            backgroundColor: '#fee2e2',
            color: '#dc2626'
          }}
        >
          <Trash2 size={16} /> Delete
        </Button>
      )}

      <Button primary onClick={handleSave} disabled={isSubmitting}>
        {isSubmitting ? (
          "Saving..."
        ) : (
          <>
            <Save size={16} /> Save Template
          </>
        )}
      </Button>
    </ButtonGroup>
  ), [handlePreviewToggle, handleSave, isSubmitting, activeId, canSendEmail]);

  return (
    <>
      <PopupComponent
        isOpen={openEmailBox}
        onClose={handleClose}
        title={activeId ? "Edit Email Template" : "Add Email Template"}
        size="lg"
        loading={isSubmitting}
        headerActions={headerActions}
      >
        <StyledEmailEditor>
          <Content>
            <FormField>
              <label>Title</label>
              <Input
                type="text"
                placeholder="Enter title"
                value={emailData.title}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleInputChange("title", e.target.value);
                }}
              />
            </FormField>

            <FormField>
              <label>Tags</label>
              <TagContainer>
                {messageTag.map((tag) => (
                  <StyledTag
                    key={tag.value}
                    active={emailData.tags[0] === tag.value}
                    onClick={() => handleTagToggle(tag.value)}
                  >
                    {tag.name.toLowerCase().replace(/_/g, " ")}
                  </StyledTag>
                ))}
              </TagContainer>
            </FormField>

            <FormField>
              <label>Subject</label>
              <Input
                type="text"
                placeholder="Enter subject"
                value={emailData.subject}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange("subject", e.target.value)}
              />
            </FormField>

            <FormField>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px'
              }}>
                <label style={{ margin: 0 }}>Message</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    type="button"
                    onClick={insertUnsubscribeLink}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      margin: 0
                    }}
                  >
                    <Link size={14} /> Unsubscribe Link
                  </Button>

                  <Button
                    type="button"
                    onClick={() => setIsFieldDrawerOpen(true)}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      margin: 0
                    }}
                  >
                    <PlusCircle size={14} /> Personalize
                  </Button>
                </div>
              </div>

              {/* Tab Switcher */}
              <TabContainer>
                <Tab
                  active={activeTab === 'richtext'}
                  onClick={() => setActiveTab('richtext')}
                >
                  <FileText />
                  Rich Text
                </Tab>
                <Tab
                  active={activeTab === 'html'}
                  onClick={() => setActiveTab('html')}
                >
                  <Code />
                  HTML Code
                </Tab>
              </TabContainer>

              {/* Content Editor */}
              {activeTab === 'richtext' ? (
                <StyledReactQuill
                  ref={editorRef}
                  value={emailData.message}
                  onChange={(content) => {
                    Logger.log('Quill Content Changed:', content);
                    handleInputChange("message", content);
                  }}
                  modules={modules}
                  placeholder="Compose your email..."
                />
              ) : (
                <HtmlEditor
                  value={emailData.message}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                    handleInputChange("message", e.target.value);
                  }}
                  placeholder="<!DOCTYPE html>
<html>
  <head>
    <meta charset=&quot;UTF-8&quot;>
    <title>Email Template</title>
  </head>
  <body>
    <!-- Your email content here -->
  </body>
</html>"
                />
              )}
            </FormField>
          </Content>
        </StyledEmailEditor>
      </PopupComponent>

      {/* Preview Modal */}
      {previewData.isOpen && (
        <EmailPreview
          isOpen={previewData.isOpen}
          onClose={() => setPreviewData(prev => ({ ...prev, isOpen: false }))}
          subject={previewData.subject}
          content={previewData.content}
          attachments={previewData.attachments}
        />
      )}

      {/* Test Email Modal */}
      {showTestEmailModal && (
        <PopupComponent
          isOpen={showTestEmailModal}
          onClose={() => {
            setShowTestEmailModal(false);
            setTestEmailAddress('');
          }}
          title="Send Test Email"
          size="sm"
        >
          <TestEmailModal>
            <p style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
              Send a test email to verify your template looks correct.
            </p>
            <TestEmailInput
              type="email"
              placeholder="recipient@example.com"
              value={testEmailAddress}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setTestEmailAddress(e.target.value)}
              onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  handleSendTestEmail();
                }
              }}
            />
            <TestEmailActions>
              <Button onClick={() => {
                setShowTestEmailModal(false);
                setTestEmailAddress('');
              }}>
                Cancel
              </Button>
              <Button
                primary
                onClick={handleSendTestEmail}
                disabled={isSendingTestEmail}
              >
                <Mail size={16} />
                {isSendingTestEmail ? 'Sending...' : 'Send Test'}
              </Button>
            </TestEmailActions>
          </TestEmailModal>
        </PopupComponent>
      )}

      {/* Unsaved Changes Modal */}
      <GenericConfirmationModal
        showModal={showConfirmModal}
        toggleModal={() => setShowConfirmModal(false)}
        onConfirmation={handleConfirmClose}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close?"
        confirmText="Yes, Close"
        cancelText="Cancel"
        isWarning={true}
      />

      {/* Personalization Field Drawer */}
      <PopupComponent
        isOpen={isFieldDrawerOpen}
        onClose={() => setIsFieldDrawerOpen(false)}
        title="Insert Personalization Field"
        size="sm"
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '400px',
            overflowY: 'auto',
            overflowX: 'hidden',
            width: '100%'
          }}
        >
          {(availableFields.length > 0 ? availableFields : reduxFields).map((field: any) => (
            <Button
              key={field.value}
              onClick={() => handleFieldInsertion({
                value: field.value,
                label: field.label || field.name
              })}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                marginBottom: '8px',
                padding: '10px',
                background: '#f0f0f0',
                border: 'none',
                borderRadius: '4px'
              }}
            >
              {field.label || field.name}
            </Button>
          ))}
        </div>
      </PopupComponent>

      {/* Delete Confirmation Modal */}
      <GenericConfirmationModal
        showModal={showDeleteModal}
        toggleModal={() => setShowDeleteModal(false)}
        onConfirmation={handleDelete}
        title="Delete Email Template"
        message="Are you sure you want to delete this email template? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isWarning={true}
      />
    </>
  );
};

export default EmailDashboard;
